import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { ENTITY_LIST_QUERY } from '../../graphql/entityList'
import {
  BULK_UPDATE_FEATURE_FLAG_MUTATION,
  FEATURES_QUERY
} from '../../graphql/featureFlag'
import { ROLES_QUERY } from '../../graphql/role'
import { getTranslate } from '../../localization'
import { EntityList } from '../../types/entityList'
import {
  BulkUpdateFeatureFlagInput,
  RoleFeature
} from '../../types/featureFlag'
import { RoleBrief } from '../../types/role'
import FeatureBox from './featureBox'
import FeatureFilters from './filters'
import useStyles from './styles/index.styles'

function FeatureFlagBulkUpdate() {
  const classes = useStyles()
  const [autoCompleteForRole, setAutoCompleteForRele] = useState<any>(null)
  const [autoCompleteForEntity, setAutoCompleteForEntity] = useState<any>(null)
  const [bulkUpdateFeatureFlagInput, setBulkUpdateFeatureFlagInput] =
    useState<BulkUpdateFeatureFlagInput>({
      roleID: '',
      featureIDs: [],
      entityCodes: [],
      assignmentType: 'Assign'
    })

  const [initialFeatures, setInitialFeatures] = useState<RoleFeature[]>([])
  const [filteredFeatures, setFilteredFeatures] = useState<RoleFeature[]>([])

  const { data: rolesData } = useQuery<{
    roles: RoleBrief[]
  }>(ROLES_QUERY, {
    variables: { filters: {} },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [getEntityList, { data: entityListData }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [bulkUpdateFeature, { loading: loadingForBulkUpdateFeature }] =
    useMutation(BULK_UPDATE_FEATURE_FLAG_MUTATION, {
      notifyOnNetworkStatusChange: true,
      onError: (err) => {
        toast.error(err.message)
      }
    })

  const [getFeatures, { loading: loadingForFeatures }] = useLazyQuery<{
    features: RoleFeature[]
  }>(FEATURES_QUERY, {
    variables: { filters: {} },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setInitialFeatures(res.features)
    }
  })

  useEffect(() => {
    if (autoCompleteForRole) {
      getFeatures({ variables: {} })
    }
    if (autoCompleteForRole && autoCompleteForRole.value)
      getEntityList({
        variables: {
          entityType: autoCompleteForRole.value
        }
      })
  }, [autoCompleteForRole])

  return (
    <div className={classes.main}>
      <div className={classes.root}>
        <PageHeader title={getTranslate('Features Flag Bulk Update')} />
        <div className={classes.header}>
          <Typography variant="h3" gutterBottom>
            {getTranslate('Features Flag Bulk Update')}
          </Typography>
        </div>
        <FeatureFilters
          autoCompleteForRole={autoCompleteForRole}
          setAutoCompleteForRele={setAutoCompleteForRele}
          autoCompleteForEntity={autoCompleteForEntity}
          setAutoCompleteForEntity={setAutoCompleteForEntity}
          bulkUpdateFeatureFlagInput={bulkUpdateFeatureFlagInput}
          setBulkUpdateFeatureFlagInput={setBulkUpdateFeatureFlagInput}
          roles={rolesData?.roles || []}
          entityList={entityListData?.entityList || []}
          initialFeatures={initialFeatures}
          setFilteredFeatures={setFilteredFeatures}
        />
        <Typography className={classes.subtitle}>
          {getTranslate('Features List')}
        </Typography>
        <div className={classes.content}>
          {loadingForFeatures ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={36} />
            </div>
          ) : (
            filteredFeatures.map((roleFeature) => (
              <FeatureBox
                featuresGroup={roleFeature}
                bulkUpdateFeatureFlagInput={bulkUpdateFeatureFlagInput}
                setBulkUpdateFeatureFlagInput={setBulkUpdateFeatureFlagInput}
              />
            ))
          )}
        </div>
      </div>
      <div className={classes.footer}>
        <div>
          <Typography className={classes.title}>
            {getTranslate('Unsaved Changes')}
          </Typography>
          <Typography className={classes.subTitle}>
            {getTranslate('You have pending changes that have not been saved')}
          </Typography>
        </div>
        <div className={classes.btnContainer}>
          <Button
            variant="outlined"
            color="primary"
            className={classes.cancelBtn}>
            <Typography className={classes.redText}>
              {getTranslate('Cancel')}
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              bulkUpdateFeature({
                variables: { input: bulkUpdateFeatureFlagInput }
              })
            }}
            disabled={!autoCompleteForRole || initialFeatures.length < 1}>
            <Typography className={classes.whiteText}>
              {getTranslate('Save Changes')}
            </Typography>
            {loadingForBulkUpdateFeature && (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
                color="primary"
              />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FeatureFlagBulkUpdate
